import { useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, type IPagedCollection } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IFormFieldOption } from '../../viewModels/form';

/** One choice for an Option / MultipleOptions lead attribute. */
export type LeadAttributeOption = {
	id: string;
	label: string;
	/** Option color, when the CRM property defines one. */
	hexColor?: string;
};

export type UseLeadAttributeOptionsResult = {
	options: LeadAttributeOption[];
	isLoading: boolean;
	error: Error | null;
};

/**
 * Lazy-loads the choices for a single lead `attributesForm` Option /
 * MultipleOptions field. Mirrors {@link useDealFieldOptions} but against the
 * lead attributes form base URL — BE-confirmed endpoint
 * `GET lead/{companyId}/Lead/Field/{fieldId}` (#18844). First page only; option
 * lists fit comfortably under the default page size.
 *
 * Gated on `open` so options load on demand when the rep opens a pill's
 * dropdown, not for every attribute pill on every lead.
 */
export function useLeadAttributeOptions(
	companyId: string | undefined,
	fieldId: string | undefined,
	open: boolean
): UseLeadAttributeOptionsResult {
	const userSession = useUserSession();

	const query = useQuery<LeadAttributeOption[], Error>({
		queryKey: ['focused-view', 'lead-attributes', 'field-options', companyId, fieldId] as const,
		enabled: !!companyId && !!fieldId && open,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<IPagedCollection<IFormFieldOption>>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/Lead/Field/${fieldId}` }),
				'GET'
			);
			return (response.values ?? []).map(option => ({
				id: option.id ?? '',
				label: option.label ?? '',
				hexColor: option.hexColor,
			}));
		},
		// Surface fetch failures via the global QueryCache.onError toast — without
		// this a failed options fetch silently renders "No options" as if the field
		// genuinely had none (#18844 review; LevStandardQueryMeta pattern).
		meta: { toastErrorMessage: 'Failed to load field options' },
	});

	return {
		options: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
